import { useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Box, Card, CardContent, Typography } from "../../../../components";
import { Divider, Skeleton } from "@mui/material";
import { MonitorFinanceiroService } from "../../../../services";
import { formatPrice, responseErros } from "../../../../utils";

const monitorFinanceiroService = new MonitorFinanceiroService();

const defaultData = {
  itens: [],
  resumo: {
    receitaBruta: 0,
    faturamentoLiquido: 0,
    margemContribuicao: 0,
    lucroLiquido: 0,
    margemLiquidaPct: 0,
  },
};

const CORES = {
  receita: "#1976d2",
  deducao: "#f57c00",
  custo: "#d32f2f",
  subtotal: "#0288d1",
};

function corItem(item) {
  if (item.tipo === "resultado") {
    return item.valor >= 0 ? "#388e3c" : "#d32f2f";
  }
  return CORES[item.tipo] || "#94a3b8";
}

function MonitorFinDre({ filtrosBody }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(defaultData);

  useEffect(() => {
    buscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtrosBody]);

  async function buscar() {
    setLoading(true);
    const result = await monitorFinanceiroService.getDashboardDre(filtrosBody);
    if (!result.isAxiosError) {
      setData(result.data || defaultData);
    } else {
      responseErros(result);
    }
    setLoading(false);
  }

  const itens = data.itens || [];
  const resumo = data.resumo || defaultData.resumo;
  const semDados = !itens.length || itens.every((i) => !i.valor);
  const lucroPositivo = (resumo.lucroLiquido || 0) >= 0;

  const chartOptions = {
    chart: { type: "bar", height: 380, spacing: [10, 10, 10, 10] },
    title: { text: "" },
    credits: { enabled: false },
    xAxis: {
      categories: itens.map((i) => i.label),
      labels: { style: { fontSize: "11px" } },
    },
    yAxis: {
      title: { text: "" },
      labels: {
        style: { fontSize: "10px" },
        formatter: function () {
          return formatPrice(this.value);
        },
      },
    },
    legend: { enabled: false },
    tooltip: {
      useHTML: true,
      backgroundColor: "#fff",
      borderWidth: 0,
      borderRadius: 4,
      formatter: function () {
        return `<b>${this.x}</b><br/>${formatPrice(this.y)}`;
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 3,
        dataLabels: {
          enabled: true,
          style: { fontSize: "10px", fontWeight: "600", textOutline: "none" },
          formatter: function () {
            return formatPrice(this.y);
          },
        },
      },
    },
    series: [
      {
        name: "DRE",
        colorByPoint: true,
        data: itens.map((i) => ({ y: i.valor, color: corItem(i) })),
      },
    ],
  };

  return (
    <Card sx={{ borderRadius: 3, border: "1px solid #f0f0f0", height: "100%" }}>
      <CardContent sx={{ p: 2 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={1}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              DRE Gerencial
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Demonstração do Resultado no período
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            <Box textAlign="right">
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.1,
                  color: lucroPositivo ? "#388e3c" : "#d32f2f",
                }}
              >
                {formatPrice(resumo.lucroLiquido)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Lucro Líq. Operacional · Margem{" "}
                {(resumo.margemLiquidaPct || 0).toFixed(1).replace(".", ",")}%
              </Typography>
            </Box>
            <i
              className="ph-bold ph-chart-bar-horizontal"
              style={{ color: "#64748b", fontSize: 20 }}
            />
          </Box>
        </Box>
        <Divider sx={{ mb: 2 }} />
        {loading ? (
          <Skeleton
            variant="rectangular"
            height={360}
            sx={{ borderRadius: 1 }}
          />
        ) : semDados ? (
          <Box display="flex" justifyContent="center" py={6}>
            <Typography variant="body2" color="text.secondary">
              Sem dados de DRE no período selecionado.
            </Typography>
          </Box>
        ) : (
          <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        )}
      </CardContent>
    </Card>
  );
}

export default MonitorFinDre;
